import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import '@/App.css';
import QuickCaptureModal from './components/quick-capture-modal';
import { setQuickCaptureFile } from './lib/quick-capture-store';
import FeedbackWidget from './components/feedback-widget';

// Pages
import LoginPage from './pages/LoginPage';
import LandingPage from './pages/public/LandingPage';
import FeaturesPage from './pages/public/FeaturesPage';
import DashboardLayout from './components/DashboardLayout';
import EmployeeDashboard from './pages/employee/EmployeeDashboard';
import LogReports from './pages/employee/LogReports';
import ProfilePage from './pages/employee/ProfilePage';
import VacationPlannerPage from './pages/employee/VacationPlannerPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import ProjectsPage from './pages/admin/ProjectsPage';
import DeviationsPage from './pages/admin/DeviationsPage';
import EmployeesPage from './pages/admin/EmployeesPage';
import ApprovalsPage from './pages/admin/ApprovalsPage';
import ExpenseApprovalsPage from './pages/admin/ExpenseApprovalsPage';
import ReportsPage from './pages/admin/ReportsPage';
import AnalyticsPage from './pages/admin/AnalyticsPage';
import BillingReportPage from './pages/admin/BillingReportPage';
import MonthlyReportsPage from './pages/admin/MonthlyReportsPage';
import VacationCalendarPage from './pages/admin/VacationCalendarPage';
import ExpenseSettingsPage from './pages/admin/ExpenseSettingsPage';
import CompanyPage from './pages/admin/CompanyPage';
import IntegrationsPage from './pages/admin/IntegrationsPage';
import SupplierInvoicesPage from './pages/admin/SupplierInvoicesPage';
import InboundDocumentsPage from './pages/admin/InboundDocumentsPage';
import ClientsPage from './pages/admin/ClientsPage';
import InvoicesPage from './pages/admin/InvoicesPage';
import InvoiceDetailPage from './pages/admin/InvoiceDetailPage';
import SuperAdminLayout from './pages/superadmin/SuperAdminLayout';
import CompaniesPage from './pages/superadmin/CompaniesPage';
import SuperAdminAuditLogPage from './pages/superadmin/SuperAdminAuditLogPage';

const SuperAdminGuard = ({ children }) => {
    const { isAuthenticated, loading, isSuperAdmin } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="w-8 h-8 border-2 border-foreground border-t-transparent animate-spin mx-auto mb-4"></div>
                    <p className="text-sm text-muted-foreground uppercase tracking-widest">Loading</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (!isSuperAdmin) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

const ProtectedRoute = ({ children, adminOnly = false, ownerOnly = false, allowAccountant = false }) => {
    const { isAuthenticated, loading, isAdmin, isOwner, isAccountant } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="w-8 h-8 border-2 border-foreground border-t-transparent animate-spin mx-auto mb-4"></div>
                    <p className="text-sm text-muted-foreground uppercase tracking-widest">Loading</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    if (ownerOnly && !isOwner) {
        return <Navigate to="/dashboard" replace />;
    }

    if (adminOnly && !isAdmin && !(allowAccountant && isAccountant)) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

const AppRoutes = () => {
    const { isAuthenticated, isAdmin, loading } = useAuth();
    const { i18n } = useTranslation();

    // Read `?quick-capture=receipt` synchronously at component init time, before
    // the `<Navigate to="/dashboard">` redirect clears the URL. Lazy initial state
    // guarantees this runs once, before the first render, so we capture the param
    // even though React Router's Navigate replaces the URL on the same paint.
    const [quickCaptureOpen, setQuickCaptureOpen] = useState(() => {
        if (typeof window === 'undefined') return false;
        const param = new URLSearchParams(window.location.search).get('quick-capture');
        if (param === 'receipt') {
            // Clean immediately so refreshing doesn't re-trigger
            window.history.replaceState({}, '', window.location.pathname);
            return true;
        }
        return false;
    });

    const navigate = useNavigate();

    const handleQuickCaptureFile = (file) => {
        setQuickCaptureOpen(false);
        setQuickCaptureFile(file);
        navigate('/dashboard/log-reports');
    };

    // Detect ?share-receipt=1 written by the service worker after a Web Share
    // Target POST. The file is stored in Cache API under '__share_target_file__'
    // and must be retrieved asynchronously, so we use useEffect rather than
    // the synchronous lazy-state approach used for ?quick-capture above.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        var params = new URLSearchParams(window.location.search);
        if (params.get('share-receipt') !== '1') return;

        // Clean the param immediately so a hard-refresh does not re-trigger
        window.history.replaceState({}, '', window.location.pathname);

        if (!('caches' in window)) return;

        caches.open('hisland-time-v2').then(function (cache) {
            return cache.match('__share_target_file__').then(function (response) {
                if (!response) return;
                cache.delete('__share_target_file__');
                var filename = response.headers.get('X-File-Name') || 'shared-file';
                return response.blob().then(function (blob) {
                    var file = new File([blob], filename, { type: blob.type });
                    handleQuickCaptureFile(file);
                });
            });
        });
        // handleQuickCaptureFile is stable (defined above, no deps that change)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        document.documentElement.lang = i18n.language.startsWith('sv') ? 'sv' : 'en';
    }, [i18n.language]);

    useEffect(() => {
        if (!('serviceWorker' in navigator)) return;
        const onControllerChange = () => {
            toast('New version available', {
                description: 'Reload to get the latest updates.',
                duration: Infinity,
                action: {
                    label: 'Reload',
                    onClick: () => window.location.reload(),
                },
            });
        };
        navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
        return () => navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="w-8 h-8 border-2 border-foreground border-t-transparent animate-spin mx-auto mb-4"></div>
                    <p className="text-sm text-muted-foreground uppercase tracking-widest">Loading</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <QuickCaptureModal
                open={quickCaptureOpen}
                onClose={() => setQuickCaptureOpen(false)}
                onFileSelected={handleQuickCaptureFile}
            />
            {isAuthenticated && !loading && <FeedbackWidget />}
        <Routes>
            <Route
                path="/login"
                element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />}
            />
            
            <Route
                path="/"
                element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingPage />}
            />

            {/* Public features page — accessible to everyone, including authenticated users */}
            <Route path="/features" element={<FeaturesPage />} />

            <Route
                path="/dashboard"
                element={
                    <ProtectedRoute>
                        <DashboardLayout />
                    </ProtectedRoute>
                }
            >
                <Route 
                    index 
                    element={isAdmin ? <AdminDashboard /> : <EmployeeDashboard />} 
                />
                
                {/* Employee routes */}
                <Route path="log-reports" element={<LogReports />} />
                {/* Legacy path redirects — Phase 5 5.28 renamed /timesheet to
                    /log-reports; Phase 5 5.53 retired the standalone /expenses
                    page (its add/submit/history functions all live on the
                    calendar page now). Kept so old bookmarks/links don't 404. */}
                <Route path="timesheet" element={<Navigate to="/dashboard/log-reports" replace />} />
                <Route path="expenses" element={<Navigate to="/dashboard/log-reports" replace />} />
                <Route path="vacation-planner" element={<VacationPlannerPage />} />
                <Route path="profile" element={<ProfilePage />} />
                
                {/* Admin routes */}
                {/* Settings hosts EIGHT admin sections and ONE that an
                    accountant is entitled to: the Fortnox mapping (Task 22.9).
                    `allowAccountant` here only gets them through the door —
                    it is NOT the access decision, because the section is
                    chosen by a `?tab=` query parameter that the user controls.
                    AdminSettingsPage itself pins a non-admin accountant to
                    fortnox-mapping and renders nothing else. Widening this
                    route without that guard would hand an accountant payroll,
                    cost codes and employees. */}
                <Route
                    path="settings"
                    element={
                        <ProtectedRoute adminOnly allowAccountant>
                            <AdminSettingsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="projects"
                    element={
                        <ProtectedRoute adminOnly>
                            <ProjectsPage />
                        </ProtectedRoute>
                    }
                />
                <Route 
                    path="deviations" 
                    element={
                        <ProtectedRoute adminOnly>
                            <DeviationsPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="employees" 
                    element={
                        <ProtectedRoute adminOnly>
                            <EmployeesPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="approvals" 
                    element={
                        <ProtectedRoute adminOnly>
                            <ApprovalsPage />
                        </ProtectedRoute>
                    } 
                />
                <Route 
                    path="expense-approvals" 
                    element={
                        <ProtectedRoute adminOnly>
                            <ExpenseApprovalsPage />
                        </ProtectedRoute>
                    } 
                />
                <Route
                    path="reports"
                    element={
                        <ProtectedRoute adminOnly allowAccountant>
                            <ReportsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="billing"
                    element={
                        <ProtectedRoute adminOnly allowAccountant>
                            <BillingReportPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="monthly-reports"
                    element={
                        <ProtectedRoute adminOnly allowAccountant>
                            <MonthlyReportsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="analytics"
                    element={
                        <ProtectedRoute adminOnly allowAccountant>
                            <AnalyticsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="supplier-invoices"
                    element={
                        <ProtectedRoute adminOnly allowAccountant>
                            <SupplierInvoicesPage />
                        </ProtectedRoute>
                    }
                />
                {/* Inbound documents (Phase 25.4) — the holding area that feeds
                    Supplier Invoices. Gated identically, because the backend
                    inbound-document routes use require_accountant_or_admin,
                    the same guard the supplier-invoice routes use. */}
                <Route
                    path="inbound-documents"
                    element={
                        <ProtectedRoute adminOnly allowAccountant>
                            <InboundDocumentsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="clients"
                    element={
                        <ProtectedRoute adminOnly allowAccountant>
                            <ClientsPage />
                        </ProtectedRoute>
                    }
                />
                {/* Invoices (Phase 21). Gated exactly like Clients and Supplier
                    Invoices — the backend invoice routes all use
                    require_accountant_or_admin, so an accountant must reach
                    both the list and one invoice. The detail route is listed
                    after the list route; they cannot collide, but keeping the
                    static path first is the convention in this file. */}
                <Route
                    path="invoices"
                    element={
                        <ProtectedRoute adminOnly allowAccountant>
                            <InvoicesPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="invoices/:id"
                    element={
                        <ProtectedRoute adminOnly allowAccountant>
                            <InvoiceDetailPage />
                        </ProtectedRoute>
                    }
                />
                <Route 
                    path="vacation-calendar" 
                    element={
                        <ProtectedRoute adminOnly>
                            <VacationCalendarPage />
                        </ProtectedRoute>
                    } 
                />
                <Route
                    path="expense-settings"
                    element={
                        <ProtectedRoute adminOnly>
                            <ExpenseSettingsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="company"
                    element={
                        <ProtectedRoute ownerOnly>
                            <CompanyPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="integrations"
                    element={
                        <ProtectedRoute ownerOnly>
                            <IntegrationsPage />
                        </ProtectedRoute>
                    }
                />
            </Route>

            <Route
                path="/superadmin"
                element={
                    <SuperAdminGuard>
                        <SuperAdminLayout />
                    </SuperAdminGuard>
                }
            >
                <Route index element={<Navigate to="/superadmin/companies" replace />} />
                <Route path="companies" element={<CompaniesPage />} />
                <Route path="audit-log" element={<SuperAdminAuditLogPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </>
    );
};

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <AppRoutes />
                <Toaster position="top-right" />
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
